// LWZO (docs/02-konzept.md E27): Benutzerliste der Admin-Oberflaeche.
// Upstream zeigte hier eine Tabelle mit einem Lese-Modal; jetzt ist es die
// Liste mit Suche, Anlegen-Knopf, Bearbeiten (UserPage.tsx) und Loeschen.
import { mdiPencil, mdiPlusCircle } from "@mdi/js";
import Icon from "@mdi/react";
import React, { useEffect, useMemo, useState } from "react";
import { Nav } from "../account/Nav";
import { idpac } from "../api";
import { AuthContext, User, User_Status, UserProfile } from "../generated/idp/api/idp";
import { Input } from "../Input";
import { FakeUserImage } from "../login/FakeUserImage";
import { nanoid } from "../nanoid";
import { goToLocation } from "../router";
import {
  Chip,
  collator,
  ConfirmingDeleteButton,
  ErrorText,
  IconButton,
  needsLogin,
  StripedTBody,
} from "./AdminUI";

export function userEmails(u: User): string[] {
  return (u.emailAddress ?? []).map((e) => `${e.localPart}@${e.domain}`);
}

export function statusLabel(s: User_Status): string {
  switch (s) {
    case User_Status.OK:
      return "Aktiv";
    case User_Status.DISABLED:
      return "Gesperrt";
    default:
      return User_Status[s] ?? String(s);
  }
}

export function Users(props: {
  profile?: UserProfile | null;
  onLogout: () => void;
  authenticate: (a: AuthContext) => void;
}): JSX.Element {
  const [users, setUsers] = useState<User[] | null>(null);
  const [filter, setFilter] = useState("");
  const [error, setError] = useState<string | null>(null);
  const authenticate = props.authenticate;

  useEffect(() => {
    let ignore = false;
    const fetchData = async () => {
      try {
        const res = await idpac.ListUsers({});
        if (!ignore) {
          const list = [...(res.user ?? [])];
          list.sort((a, b) => collator.compare(a.profile?.name ?? a.id, b.profile?.name ?? b.id));
          setUsers(list);
        }
      } catch (err) {
        if (needsLogin(err, authenticate)) {
          return;
        }
        if (!ignore) {
          setError(String(err?.message ?? err));
        }
      }
    };
    fetchData();
    return () => {
      ignore = true;
    };
  }, [authenticate]);

  // Suche ueber Name, Adresse, Gruppe und ID - alles, was man von einem
  // Konto kennt, wenn jemand anruft.
  const shown = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (q === "" || users === null) {
      return users ?? [];
    }
    return users.filter((u) => {
      const hay = [u.profile?.name ?? "", u.id, ...userEmails(u), ...(u.memberOf ?? [])]
        .join("\n")
        .toLowerCase();
      return hay.includes(q);
    });
  }, [users, filter]);

  return (
    <Nav profile={props.profile ?? undefined} onLogout={props.onLogout}>
      <h2>
        Benutzer&nbsp;
        <IconButton
          title="Neuen Benutzer anlegen"
          onClick={() => {
            goToLocation({ pathname: "/admin/users/" + nanoid(), search: "?new" });
          }}
        >
          <Icon size={1} path={mdiPlusCircle} />
        </IconButton>
      </h2>
      <div style={{ maxWidth: 480 }}>
        <Input name="suche" label="Suchen (Name, E-Mail, Gruppe, ID)" value={filter} onChange={setFilter} />
      </div>
      {error && <ErrorText>{error}</ErrorText>}
      <p style={{ color: "#666" }}>
        {users === null ? "Lade…" : `${shown.length} von ${users.length} Konten`}
      </p>
      <table style={{ width: "100%" }}>
        <thead style={{ textAlign: "left" }}>
          <tr>
            <th></th>
            <th>Name</th>
            <th>E-Mail</th>
            <th>Gruppen</th>
            <th>Status</th>
            <th>Aktionen</th>
          </tr>
        </thead>
        <StripedTBody>
          {shown.map((u) => (
            <tr key={u.id}>
              <td>
                {u.profile?.picture ? (
                  <img alt={u.profile?.name} src={u.profile?.picture} height="24" />
                ) : (
                  <FakeUserImage name={u.profile?.name ?? u.id} size={24} />
                )}
              </td>
              <td>
                {u.profile?.name || <i>ohne Namen</i>}
                {u.external ? <span title="Extern verwaltet"> ⧉</span> : null}
              </td>
              <td>{userEmails(u).join(", ")}</td>
              <td>
                {(u.memberOf ?? []).map((g) => (
                  <Chip key={g}>{g}</Chip>
                ))}
              </td>
              <td>{statusLabel(u.status)}</td>
              <td style={{ whiteSpace: "nowrap" }}>
                <IconButton title="Bearbeiten" onClick={() => goToLocation("/admin/users/" + u.id)}>
                  <Icon size={1} path={mdiPencil} />
                </IconButton>
                {!u.external && (
                  <ConfirmingDeleteButton
                    onDelete={async () => {
                      try {
                        await idpac.DeleteUser({ userId: u.id });
                        setUsers((old) => (old ?? []).filter((x) => x.id !== u.id));
                      } catch (err) {
                        if (!needsLogin(err, authenticate)) {
                          setError(String(err?.message ?? err));
                        }
                      }
                    }}
                  />
                )}
              </td>
            </tr>
          ))}
        </StripedTBody>
      </table>
    </Nav>
  );
}
